import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { DateField } from '@/components/form/fields/date/DateInput';
import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import SiTrendingUpFill from '@/icons/SiTrendingUpFill';
import { useAddPriceAndMargin, useBaseMargin, usePrice } from '@/services/api/hooks/margins';
import { GBP } from '@dinero.js/currencies';
import { zodResolver } from '@hookform/resolvers/zod';

import { dinero } from 'dinero.js';
import { FC, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import LocalizedStrings from 'react-localization';
import { toast } from 'sonner';
import { PriceAndMarginForm, PriceAndMarginFormFields } from './PriceAndMarginForm';

const strings = new LocalizedStrings({
  en: {
    title: 'Change cost & margin',
    createButton: 'Create',
    successMessage: 'Price and margin updated',
    errorMessage: 'No product id provided'
  }
});

export const AddPriceAndMarginModal: FC<ModalProps<{ productId: string }>> = props => {
  const AddPriceAndMarginForm = () => {
    const productId = props.props?.productId;
    if (!productId) throw new Error(strings.errorMessage);
    const [baseMargin] = useBaseMargin(productId);
    const [price] = usePrice(productId);
    const addPriceAndMargin = useAddPriceAndMargin();

    const methods = useForm<PriceAndMarginFormFields>({
      resolver: zodResolver(PriceAndMarginFormFields),
      defaultValues: {
        price: dinero({ amount: price.price, currency: GBP }),
        modifier: String(baseMargin.modifier),
        effectiveDate: new Date()
      }
    });

    const onSubmit = async (data: PriceAndMarginFormFields) => {
      const price = data.price.toJSON();
      await addPriceAndMargin.mutateAsync({
        productId,
        price: price.amount,
        modifier: Number(data.modifier),
        min: 0,
        max: 10000000,
        effectiveDate: {
          value: data.effectiveDate ?? new Date()
        }
      });

      toast.success(strings.successMessage);
      props.close();
    };

    return (
      <Form className="grid w-full grid-cols-2 gap-2" methods={methods} onSubmit={onSubmit}>
        <PriceAndMarginForm />
        <DateField className="col-span-2" id="effectiveDate" label="Date" />
        <Button className="col-span-2 w-fit place-self-end" variant={ButtonVariant.Primary} type="submit">
          {strings.createButton}
        </Button>
      </Form>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Card className="flex w-full flex-col gap-2 p-10 pb-12">
        <Modal.Controls {...props}>
          <h1 className="flex gap-2 text-2xl font-semibold">
            <span className="bg-primary-std rounded-full p-1 text-2xl">
              <SiTrendingUpFill />
            </span>
            <span>{strings.title}</span>
          </h1>
        </Modal.Controls>
        <Suspense fallback={<Loading />}>
          <AddPriceAndMarginForm />
        </Suspense>
      </Card>
    </Modal>
  );
};
